import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import StopIcon from '@mui/icons-material/Stop'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import {
  Button,
  Box,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material'
import { MainApi } from 'api/MainApi'
import { RobotApi } from 'api/RobotApi'
import { MainService, MissionJobRead, MissionJobTypeEnum, OdysseyRead, RobotRead } from 'api/schema'
import { Button as CustomButton } from 'components/Button/Button'
import Title from 'components/Title/Title'
import addDays from 'date-fns/addDays'
import endOfDay from 'date-fns/endOfDay'
import endOfMonth from 'date-fns/endOfMonth'
import endOfWeek from 'date-fns/endOfWeek'
import format from 'date-fns/format'
import getDay from 'date-fns/getDay'
import enUS from 'date-fns/locale/en-US'
import parse from 'date-fns/parse'
import startOfDay from 'date-fns/startOfDay'
import startOfMonth from 'date-fns/startOfMonth'
import startOfWeek from 'date-fns/startOfWeek'
import {
  computeSchedulerFilterOptions,
  matchesFilter,
} from 'features/tasksNormalized/filterEngine'
import type {
  FilterableRecord,
  FilterEngineContext,
  NormalizedStatisticsApiRow,
  TasksNormalizedFilterState,
} from 'features/tasksNormalized/filterableRecord'
import { filterableRecordToProcessedEvent } from 'features/tasksNormalized/filterableToCalendarEvent'
import {
  enrichFilterableFacilityIds,
  normalizedStatisticsRowToFilterable,
} from 'features/tasksNormalized/normalizedToFilterable'
import {
  ProcessedEvent,
  getColorByColorName,
  getColorById,
  getLoadingStatus,
  getTaskId,
  getTaskPrefix,
  renderMissions,
  renderScenario,
  renderStatsTable,
  scheduleToEvents,
  toISOStringYYYYMMDD,
} from 'pages/TaskScheduler/TaskSchedulerUtils'
import { exportScheduleToCSV } from 'pages/TaskScheduler/TaskSchedulerExport'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { Calendar, View, Views, dateFnsLocalizer } from 'react-big-calendar'
import { useTranslation } from 'react-i18next'
import { useUser } from 'hooks/user'
import { PATH } from 'utils/constants'
import { getVendorDisplayName } from 'utils/vendorDisplayName'
import type { VendorCode } from 'utils/vendorDisplayName'

import { ConfirmationDialogAllSingle } from '../TaskScheduler/ConfirmationDialogAllSingle'
import { EventEditDeletePopup } from '../TaskScheduler/TaskSchedulerEventEditDeletePopup'
import TaskSettingsModal from '../TaskScheduler/TaskSettingsModal/TaskSettingsModal'
import schedulerStyle from '../TaskScheduler/SchedulerSettings.module.css'
import style from 'components/Button/Button.module.css'
import 'react-big-calendar/lib/css/react-big-calendar.css'

const locales = {
  'en-US': enUS,
}

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
})

enum EventScopes {
  ALL = 'all',
  CURRENT = 'current',
}

const TaskEventViewer = ({ event }: { event: any }) => {
  const [selectedReport, setSelectedReport] = useState<string>('Overall')

  const extendedProps = event.extendedProps
  const hasExtendedProps = extendedProps && extendedProps.missions_progress && extendedProps.missions_progress.length > 0
  const isOdysseyEvent = String(extendedProps?.task_type ?? '').toLowerCase().includes('odyssey')
  const missionInfoList = Array.isArray(extendedProps?.missions_info) ? extendedProps.missions_info : []

  const handleReportChange = (event: SelectChangeEvent) => {
    setSelectedReport(event.target.value as string)
  }

  const eventMissionsRaw = missionInfoList.length
    ? missionInfoList.map((m: any) => m?.id).filter((id: any) => id != null)
    : event.extendedProps?.missions
    ? event.extendedProps.missions
    : event.extendedProps?.mission_id
    ? [event.extendedProps.mission_id]
    : []
  const eventMissions = eventMissionsRaw
    .map((id: any) => String(id).trim())
    .filter((id: string) => /^\d+$/.test(id))
    .map((id: string) => Number(id))
  const hasMissionReportDropdown = Boolean(
    hasExtendedProps && isOdysseyEvent && missionInfoList.length > 1 && extendedProps.missions_progress.length > 1
  )
  const isNormalizedSingleMission =
    Boolean(event.extendedProps?.normalized) &&
    Array.isArray(event.extendedProps?.missions_info) &&
    event.extendedProps.missions_info.length === 1

  let mainDescription = event.description
  if (hasExtendedProps && selectedReport !== 'Overall') {
    mainDescription = ''
  }

  const currentStats =
    hasExtendedProps &&
    (selectedReport === 'Overall' ? extendedProps.overall_progress : extendedProps.missions_progress[Number(selectedReport)])

  const currentName =
    hasExtendedProps &&
    (selectedReport === 'Overall'
      ? extendedProps.overall_progress?.missions?.length === 1
        ? extendedProps.missions_progress?.[0]?.name
        : ''
      : extendedProps.missions_progress?.[Number(selectedReport)]?.name)

  const startDateTimeStr = format(event.start, 'EEEE, MMM do, h:mm a')
  const endDateTimeStr = format(event.end, 'EEEE, MMM do, h:mm a')

  return (
    <div style={{ whiteSpace: 'pre-line' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#666' }}>
            <PlayArrowIcon sx={{ fontSize: '1rem' }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {startDateTimeStr}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#666' }}>
            <StopIcon sx={{ fontSize: '1rem' }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {endDateTimeStr}
          </Typography>
        </Box>
      </Box>

      {hasMissionReportDropdown && (
        <Box sx={{ minWidth: 120, mb: 1, mt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="report-select-label">Report</InputLabel>
            <Select
              labelId="report-select-label"
              id="report-select"
              value={selectedReport}
              label="Report"
              onChange={handleReportChange}
            >
              <MenuItem value={'Overall'}>Overall</MenuItem>
              {extendedProps.missions_progress.map((mission: any, index: number) => {
                const missionId = missionInfoList[index]?.id ?? mission?.id
                const missionName = missionInfoList[index]?.name ?? mission?.name
                return (
                <MenuItem key={`${mission.id}_${index}`} value={index.toString()}>
                  {`${missionId ?? ''}${missionName ? ` ${missionName}` : ''}`.trim()}
                </MenuItem>
                )
              })}
            </Select>
          </FormControl>
        </Box>
      )}
      {mainDescription && <Box sx={{ fontSize: '0.875rem', mb: 0 }}>{mainDescription}</Box>}
      {hasMissionReportDropdown && selectedReport === 'Overall' && eventMissions.length > 0 && (
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          <span style={{ fontWeight: 'bold' }}>Missions:</span>{' '}
          {eventMissions.map((missionId: number, idx: number) => (
            <React.Fragment key={missionId}>
              {idx > 0 ? ', ' : ''}
              <a className={schedulerStyle.link} href={`${PATH.MISSION}${missionId}`} target="_blank" rel="noreferrer">
                {missionId}
              </a>
            </React.Fragment>
          ))}
        </Typography>
      )}
      {selectedReport === 'Overall' &&
        !isNormalizedSingleMission &&
        !hasMissionReportDropdown &&
        renderMissions(eventMissions)}

      {currentStats && (() => {
        const reportContent = renderStatsTable(currentStats)
        if (!reportContent) return null
        return (
          <Box>
            {selectedReport === 'Overall' && currentName && !isNormalizedSingleMission && (
              <Typography variant="body2" sx={{ mb: 0.0 }}>
                <span style={{ fontWeight: 'bold' }}>Mission name:</span> {currentName}
              </Typography>
            )}
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              Report:
            </Typography>
            {reportContent}
          </Box>
        )
      })()}

      {hasExtendedProps && selectedReport !== 'Overall' && (
        <Box sx={{ mt: 1 }}>
          <a
            className={schedulerStyle.link}
            href={`${PATH.MISSION}${extendedProps.missions_progress[Number(selectedReport)]?.id}`}
            target="_blank"
            rel="noreferrer"
          >
            Go to mission
          </a>
        </Box>
      )}
    </div>
  )
}

const buildRobotIdToFacilityIds = (facilities: { id: number; robots_ids?: number[] }[]): Map<number, number[]> => {
  const m = new Map<number, number[]>()
  for (const f of facilities) {
    for (const rid of f.robots_ids || []) {
      if (!m.has(rid)) m.set(rid, [])
      m.get(rid)!.push(f.id)
    }
  }
  return m
}

const TaskSchedulerNormalized = () => {
  const { t } = useTranslation()
  const user = useUser()
  const [refreshKey, setRefreshKey] = useState(0)
  const isLoadingEvents = useRef(false)
  const [eventScope, setEventScope] = useState<string>(EventScopes.CURRENT)
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [isEditConfirmationOpen, setIsEditConfirmationOpen] = useState(false)
  const [isSingleTaskEditing, setSingleTaskEditing] = useState(false)
  const [openDeleteScheduleDialog, setOpenDeleteScheduleDialog] = useState(false)
  const [deleteReason, setDeleteReason] = useState<string>('')
  const [deleteDescription, setDeleteDescription] = useState('')

  const [filterState, setFilterState] = useState<TasksNormalizedFilterState>({
    vendor: '',
    facilityId: '',
    robotSn: '',
    missionKey: '',
    startAfterHour: -1,
  })

  const dataCache = useRef({
    robots: [] as RobotRead[],
    missionsMap: new Map<number, string>(),
    facilities: [] as { id: number; name?: string | null; robots_ids?: number[] }[],
    robotIdToFacilityIds: new Map<number, number[]>(),
    fetched: { robots: false, missions: false, facilities: false },
  })

  const [normalizedRecords, setNormalizedRecords] = useState<FilterableRecord[]>([])
  const [plannedEvents, setPlannedEvents] = useState<ProcessedEvent[]>([])

  const [selectedDate, setSelectedDate] = useState<Date>(new Date())
  const [view, setView] = useState<View>(Views.MONTH)
  const [currentRange, setCurrentRange] = useState<{ start: Date; end: Date }>(() => {
    const now = new Date()
    return {
      start: startOfMonth(now),
      end: endOfMonth(now),
    }
  })

  const updateRange = useCallback(
    (date: Date, currentView: View) => {
      let start: Date
      let end: Date
      if (currentView === Views.MONTH) {
        start = startOfMonth(date)
        end = endOfMonth(date)
      } else if (currentView === Views.AGENDA) {
        start = startOfDay(date)
        end = addDays(date, 1)
      } else if (currentView === Views.WEEK) {
        start = startOfWeek(date)
        end = endOfWeek(date)
      } else {
        start = startOfDay(date)
        end = endOfDay(date)
      }

      setCurrentRange(prev => {
        if (prev.start.getTime() === start.getTime() && prev.end.getTime() === end.getTime()) {
          return prev
        }
        return { start, end }
      })
    },
    [setCurrentRange]
  )

  const handleViewChange = useCallback(
    (newView: View) => {
      setView(newView)
      updateRange(selectedDate, newView)
    },
    [selectedDate, updateRange]
  )

  const handleNavigate = useCallback(
    (newDate: Date) => {
      setSelectedDate(newDate)
      updateRange(newDate, view)
    },
    [view, updateRange]
  )

  const engineCtx: FilterEngineContext = useMemo(() => {
    const robotsBySn = new Map<string, { id: number; name: string; sn: string }>()
    for (const r of dataCache.current.robots) {
      const sn = String(r.serial_number ?? '').trim()
      if (!sn) continue
      robotsBySn.set(sn, {
        id: r.id,
        name: String(r.name ?? '').trim() || sn,
        sn,
      })
    }
    return {
      facilityMappingReady: dataCache.current.fetched.facilities,
      facilities: dataCache.current.facilities.map(f => ({
        id: f.id,
        name: String(f.name ?? '').trim() || `Facility ${f.id}`,
      })),
      robotsBySn,
    }
  }, [normalizedRecords])

  const fetchInitialData = async (force: boolean) => {
    const promises: Promise<void>[] = []
    if (force || !dataCache.current.fetched.robots) {
      promises.push(
        MainService.mainRobotList(['-id'], 1, 1000).then(res => {
          dataCache.current.robots = res.results ?? []
          dataCache.current.fetched.robots = true
        })
      )
    }
    if (force || !dataCache.current.fetched.facilities) {
      promises.push(
        MainApi.fetchFacilities(1, 1000).then(res => {
          dataCache.current.facilities = res.results ?? []
          dataCache.current.robotIdToFacilityIds = buildRobotIdToFacilityIds(dataCache.current.facilities)
          dataCache.current.fetched.facilities = true
        })
      )
    }
    if (force || !dataCache.current.fetched.missions) {
      promises.push(
        MainService.mainMissionList(['-id'], 1, 1000).then(res => {
          const map = new Map<number, string>()
          res.results?.forEach(m => {
            if (m.name) map.set(m.id, m.name)
          })
          dataCache.current.missionsMap = map
          dataCache.current.fetched.missions = true
        })
      )
    }
    if (promises.length) await Promise.all(promises)
  }

  const generatePlannedEventsFromRobots = useCallback((start: Date, end: Date): ProcessedEvent[] => {
    const eventsSet: ProcessedEvent[] = []
    for (const robot of dataCache.current.robots) {
      const facilityIds = dataCache.current.robotIdToFacilityIds.get(robot.id) ?? []
      for (const task of robot.missions_jobs) {
        if (task.job_type === MissionJobTypeEnum.DELETE_SCENARIO) continue
        const loadingStatus = task.status === 'scenario_done' ? '' : getLoadingStatus(task.status)
        const events = scheduleToEvents(
          start,
          end,
          task,
          robot.robot_color ? () => getColorByColorName(robot.robot_color || 'White') : () => getColorById(robot.id),
          <React.Fragment>{loadingStatus && <div>{loadingStatus}</div>}</React.Fragment>,
          robot,
          dataCache.current.missionsMap,
          facilityIds
        )
        for (const e of events) {
          e.extendedProps = { ...(e.extendedProps || {}), source_kind: 'planned' }
        }
        eventsSet.push(...events)
      }

      for (const odyssey of robot.odysseys) {
        if (odyssey.job_type === MissionJobTypeEnum.DELETE_SCENARIO) continue
        const loadingStatus = odyssey.status === 'scenario_done' ? '' : getLoadingStatus(odyssey.status)
        const events = scheduleToEvents(
          start,
          end,
          odyssey,
          robot.robot_color ? () => getColorByColorName(robot.robot_color || 'White') : () => getColorById(robot.id),
          <React.Fragment>
            {loadingStatus && <div>{loadingStatus}</div>}
            {odyssey?.scenario ? renderScenario(odyssey.scenario) : null}
          </React.Fragment>,
          robot,
          dataCache.current.missionsMap,
          facilityIds
        )
        for (const e of events) {
          // scheduleToEvents uses task_type='Task' for both MissionJob and Odyssey; normalize here so
          // planned-vs-executed dedup works and UI labels are consistent.
          e.extendedProps = { ...(e.extendedProps || {}), source_kind: 'planned', task_type: 'Odyssey' }
        }
        eventsSet.push(...events)
      }
    }
    return eventsSet
  }, [])

  const plannedEventToFilterable = useCallback((e: ProcessedEvent): FilterableRecord | null => {
    const props = e.extendedProps || {}
    const robotSn = String(props.robot_sn ?? '').trim()
    if (!robotSn) return null
    const missionId = typeof props.mission_id === 'number' ? props.mission_id : null
    const scenarioId = typeof props.scenario?.id === 'number' ? props.scenario.id : null
    const missionsInfo = Array.isArray(props.missions_info)
      ? props.missions_info
          .map((m: any) => ({
            id: Number(m?.id),
            name: typeof m?.name === 'string' ? m.name : undefined,
          }))
          .filter((m: { id: number }) => Number.isFinite(m.id))
      : undefined
    const missionsInfoIds = missionsInfo ? missionsInfo.map((m: { id: number }) => m.id) : []
    return {
      vendor: props.vendor,
      robotSn,
      robotId: typeof props.robot_id === 'number' ? props.robot_id : null,
      facilityIds: Array.isArray(props.facility_ids) ? props.facility_ids.filter((x: any) => Number.isFinite(x)) : [],
      missionId,
      scenarioId,
      mapId: null,
      missionsInfoIds,
      missionsInfo,
      taskId: props.task_id ?? null,
      displayName: e.title,
      startAt: e.start,
      endAt: e.end,
      sourceEventId: String(e.event_id),
    }
  }, [])

  const combinedRecords = useMemo(() => {
    const plannedRecords = plannedEvents
      .map(plannedEventToFilterable)
      .filter((x): x is FilterableRecord => Boolean(x))
    return [...normalizedRecords, ...plannedRecords]
  }, [normalizedRecords, plannedEvents, plannedEventToFilterable])

  const filterOptions = useMemo(
    () => computeSchedulerFilterOptions(combinedRecords, filterState, engineCtx),
    [combinedRecords, filterState, engineCtx]
  )
  const scenarioMissionOptions = useMemo(
    () => filterOptions.missions.filter(m => m.kind === 'scenario'),
    [filterOptions.missions]
  )
  const missionOptions = useMemo(
    () => filterOptions.missions.filter(m => m.kind === 'mission'),
    [filterOptions.missions]
  )
  const mapOptions = useMemo(
    () => filterOptions.missions.filter(m => m.kind === 'map'),
    [filterOptions.missions]
  )

  const robotColorBySn = useMemo(() => {
    const m: Record<string, string | null | undefined> = {}
    for (const r of dataCache.current.robots) {
      const sn = String(r.serial_number ?? '').trim()
      if (!sn) continue
      m[sn] = r.robot_color
    }
    return m
  }, [normalizedRecords])

  React.useEffect(() => {
    if (filterState.facilityId && !filterOptions.facilityOptions.some(f => f.id === filterState.facilityId)) {
      setFilterState(s => ({ ...s, facilityId: '' }))
    }
    if (filterState.vendor && !filterOptions.vendors.includes(filterState.vendor as VendorCode)) {
      setFilterState(s => ({ ...s, vendor: '' }))
    }
    if (filterState.robotSn && !filterOptions.robots.some(r => r.sn === filterState.robotSn)) {
      setFilterState(s => ({ ...s, robotSn: '' }))
    }
    if (filterState.missionKey && !filterOptions.missions.some(m => m.key === filterState.missionKey)) {
      setFilterState(s => ({ ...s, missionKey: '' }))
    }
  }, [filterOptions, filterState.facilityId, filterState.vendor, filterState.robotSn, filterState.missionKey])

  const filteredCalendarEvents = useMemo(() => {
    const norm = normalizedRecords
      .filter(r => matchesFilter(r, filterState, engineCtx))
      .map(r => filterableRecordToProcessedEvent(r, robotColorBySn))
      .map(e => ({ ...e, extendedProps: { ...(e.extendedProps || {}), source_kind: 'executed' } }))

    const planned = plannedEvents.filter(e => {
      const rec = plannedEventToFilterable(e)
      return rec ? matchesFilter(rec, filterState, engineCtx) : false
    })

    // Same behavior as legacy scheduler: when planned and executed match (task/odyssey + robot + day),
    // keep executed and hide planned to avoid duplicate cards.
    const executedKeys = new Set<string>()
    for (const e of norm) {
      const props = e.extendedProps || {}
      const taskId = props.task_id
      const robotSn = String(props.robot_sn ?? '').trim()
      if (taskId == null || !robotSn) continue
      const taskKind = String(props.task_type ?? 'Task').toLowerCase().includes('odyssey') ? 'odyssey' : 'task'
      const day = toISOStringYYYYMMDD(e.start)
      executedKeys.add(`${taskKind}:${String(taskId)}:${robotSn}:${day}`)
    }

    const plannedWithoutDuplicates = planned.filter(e => {
      const props = e.extendedProps || {}
      const taskId = props.task_id
      const robotSn = String(props.robot_sn ?? '').trim()
      if (taskId == null || !robotSn) return true
      const taskKind = String(props.task_type ?? 'Task').toLowerCase().includes('odyssey') ? 'odyssey' : 'task'
      const day = toISOStringYYYYMMDD(e.start)
      const key = `${taskKind}:${String(taskId)}:${robotSn}:${day}`
      return !executedKeys.has(key)
    })

    return [...norm, ...plannedWithoutDuplicates]
  }, [normalizedRecords, plannedEvents, filterState, engineCtx, robotColorBySn, plannedEventToFilterable])

  const agendaEvents = useMemo(() => {
    if (view !== Views.AGENDA) return filteredCalendarEvents

    const dayStart = startOfDay(selectedDate).getTime()
    const dayEnd = endOfDay(selectedDate).getTime()

    return filteredCalendarEvents.filter(event => {
      const s = event.start.getTime()
      const e = event.end.getTime()
      return s <= dayEnd && e >= dayStart
    })
  }, [filteredCalendarEvents, view, selectedDate])

  const eventPropGetter = useCallback(
    (event: ProcessedEvent) => ({
      style: {
        backgroundColor: event.color || '#3174ad',
        borderRadius: '0',
        opacity: view === Views.AGENDA ? 0.9 : 1,
        color: 'white',
        border: '0px',
        display: 'block',
      },
    }),
    [view]
  )

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [popoverEvent, setPopoverEvent] = useState<ProcessedEvent | null>(null)

  const handleEventClick = (event: ProcessedEvent, e: React.SyntheticEvent) => {
    const target = (e.currentTarget || e.target) as HTMLElement
    setAnchorEl(target)
    setPopoverEvent(event)
  }

  const handleClosePopover = () => {
    setAnchorEl(null)
    setPopoverEvent(null)
  }

  const getTaskById = useCallback((eventId: string): MissionJobRead | OdysseyRead | undefined => {
    const prefix = getTaskPrefix(eventId)
    const taskId = getTaskId(eventId)
    const tasks = dataCache.current.robots.flatMap(robot => robot.missions_jobs)
    const odysseys = dataCache.current.robots.flatMap(robot => robot.odysseys)
    if (prefix === 'task') return tasks.find((task: MissionJobRead) => task.id === taskId)
    if (prefix === 'odyssey') return odysseys.find((odyssey: OdysseyRead) => odyssey.id === taskId)
    return undefined
  }, [])

  const isAllValueDisabled = (selectedEvent: ProcessedEvent | null): boolean => {
    if (!selectedEvent) return true
    const task = getTaskById(String(selectedEvent.event_id))
    if (!task || !task.scheduler_settings || !task.scheduler_settings.weekdays) return true
    const weekdays = task.scheduler_settings.weekdays
    return !Object.values(weekdays).some(val => val === 'true')
  }

  const onSettingsCloseClicked = useCallback(() => {
    setIsSettingsOpen(false)
  }, [])

  const onSettingsSubmitClicked = useCallback((refresh: boolean) => {
    setIsSettingsOpen(false)
    if (refresh) setRefreshKey(v => v + 1)
  }, [])

  const handleSubmitDeleteSchedule = async (): Promise<void> => {
    try {
      if (!popoverEvent) throw new Error('Selected event is null')
      const foundTask = getTaskById(String(popoverEvent.event_id))
      if (eventScope === EventScopes.CURRENT && foundTask?.scheduler_settings?.weekdays) {
        const dateStr = toISOStringYYYYMMDD(popoverEvent.start)
        const newSchedulerSettings = {
          ...foundTask.scheduler_settings,
          except_days: [...foundTask.scheduler_settings.except_days],
          except_days_descriptions: { ...foundTask.scheduler_settings.except_days_descriptions },
          except_days_users: { ...(foundTask.scheduler_settings.except_days_users || {}) },
        }
        newSchedulerSettings.except_days.push(dateStr)
        newSchedulerSettings.except_days.sort()
        const finalDescription = deleteReason === 'Other' ? deleteDescription : deleteReason
        newSchedulerSettings.except_days_descriptions[dateStr] = finalDescription
        const userName = user?.state.firstName
          ? `${user.state.firstName} ${user.state.lastName || ''}`.trim()
          : user?.state.email || 'Unknown User'
        newSchedulerSettings.except_days_users[dateStr] = userName

        if ('mission_id' in foundTask) {
          await RobotApi.updateTheEntireJob(
            getTaskId(String(popoverEvent.event_id)),
            foundTask.mission_id,
            foundTask.robot_id,
            newSchedulerSettings,
            foundTask.name ? foundTask.name : 'Cleaning Task'
          )
        } else {
          await RobotApi.updateTheEntireOdyssey(
            getTaskId(String(popoverEvent.event_id)),
            foundTask.missions,
            foundTask.robot_id,
            newSchedulerSettings,
            foundTask.name ? foundTask.name : 'Cleaning Task'
          )
        }
      } else if (foundTask) {
        if ('mission_id' in foundTask) await RobotApi.updateMissionJobType(foundTask.id, MissionJobTypeEnum.DELETE_SCENARIO)
        else await RobotApi.updateOdysseyType(foundTask.id, MissionJobTypeEnum.DELETE_SCENARIO)
      }

      setOpenDeleteScheduleDialog(false)
      setDeleteDescription('')
      setDeleteReason('')
      setEventScope(EventScopes.CURRENT)
      setRefreshKey(v => v + 1)
    } catch (e) {
      console.error(`Failed to delete scheduled task: ${e}`)
    }
  }

  const prevRefreshCount = useRef(refreshKey)
  const getRemoteEvents = useCallback(
    async (start: Date, end: Date) => {
      const isManualRefresh = prevRefreshCount.current !== refreshKey
      prevRefreshCount.current = refreshKey

      if (isLoadingEvents.current) {
        return
      }
      isLoadingEvents.current = true

      try {
        await fetchInitialData(isManualRefresh)

        const fetchStart = addDays(start, -7)
        const fetchEnd = addDays(end, 7)

        const res = await MainApi.fetchTasksNormalized({
          start_date: format(fetchStart, "yyyy-MM-dd'T'HH:mm:ss"),
          end_date: format(fetchEnd, "yyyy-MM-dd'T'HH:mm:ss"),
        })

        const ridMap = dataCache.current.robotIdToFacilityIds
        const rows = (res.results ?? []) as NormalizedStatisticsApiRow[]
        const records: FilterableRecord[] = []
        for (const row of rows) {
          const base = normalizedStatisticsRowToFilterable(row)
          if (!base) continue
          records.push(enrichFilterableFacilityIds(base, ridMap))
        }

        setNormalizedRecords(records)
        setPlannedEvents(generatePlannedEventsFromRobots(fetchStart, fetchEnd))
      } catch (e) {
        console.error('[TaskSchedulerNormalized] load failed', e)
      } finally {
        isLoadingEvents.current = false
      }
    },
    [refreshKey, generatePlannedEventsFromRobots]
  )

  const lastFetchedRange = useRef<{ start: number; end: number; refreshCount: number }>({
    start: 0,
    end: 0,
    refreshCount: -1,
  })

  React.useEffect(() => {
    const s = currentRange.start.getTime()
    const e = currentRange.end.getTime()
    if (
      s !== e &&
      (s !== lastFetchedRange.current.start ||
        e !== lastFetchedRange.current.end ||
        refreshKey !== lastFetchedRange.current.refreshCount)
    ) {
      lastFetchedRange.current = { start: s, end: e, refreshCount: refreshKey }
      getRemoteEvents(currentRange.start, currentRange.end)
    }
  }, [currentRange, getRemoteEvents, refreshKey])

  const handleClearFilters = () => {
    setFilterState({
      vendor: '',
      facilityId: '',
      robotSn: '',
      missionKey: '',
      startAfterHour: -1,
    })
  }

  const hasActiveFilters = useMemo(() => {
    return !!(
      filterState.facilityId ||
      filterState.vendor ||
      filterState.robotSn ||
      filterState.missionKey ||
      filterState.startAfterHour >= 0
    )
  }, [filterState])

  const handleExportSchedule = () => {
    let rangeStart = startOfDay(selectedDate)
    let rangeEnd = endOfDay(selectedDate)

    if (view === Views.WEEK) {
      rangeStart = startOfWeek(selectedDate, { weekStartsOn: 1 })
      rangeEnd = endOfWeek(selectedDate, { weekStartsOn: 1 })
    } else if (view === Views.MONTH) {
      rangeStart = startOfMonth(selectedDate)
      rangeEnd = endOfMonth(selectedDate)
    }

    const start = rangeStart.getTime()
    const end = rangeEnd.getTime()

    const exportEvents = filteredCalendarEvents.filter(event => {
      const s = event.start.getTime()
      const e = event.end.getTime()
      return s <= end && e >= start
    })

    exportScheduleToCSV(exportEvents)
  }

  return (
    <>
      <div className={schedulerStyle.mainView}>
        <div className={schedulerStyle.titleBlock}>
          <Title text={t('header.schedule_normalized')} />
          <div style={{ display: 'flex', gap: '16px' }}>
            <CustomButton.Outlined
              className={style.button}
              style={{ width: '150px' }}
              onClick={handleExportSchedule}
            >
              Export Schedule
            </CustomButton.Outlined>
            <CustomButton.Standart
              className={style.button}
              style={{ width: '250px' }}
              onClick={() => {
                setIsSettingsOpen(true)
              }}
            >
              Add a Task
            </CustomButton.Standart>
            {/* intentionally no extra hints here */}
          </div>
        </div>

        <div className={schedulerStyle.filtersBlock}>
          <div className={schedulerStyle.filters}>
            <select
              className={`${schedulerStyle.filterSelect} ${schedulerStyle.filterFacility}`}
              value={filterState.facilityId}
              onChange={e => setFilterState(s => ({ ...s, facilityId: e.target.value }))}
            >
              <option value="">All facilities</option>
              {filterOptions.facilityOptions.map(f => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>

            <select
              className={`${schedulerStyle.filterSelect} ${schedulerStyle.filterVendor}`}
              value={filterState.vendor}
              onChange={e => setFilterState(s => ({ ...s, vendor: e.target.value as VendorCode | '' }))}
            >
              <option value="">All vendors</option>
              {filterOptions.vendors.map(v => (
                <option key={v} value={v}>
                  {getVendorDisplayName(v)}
                </option>
              ))}
            </select>

            <select
              className={`${schedulerStyle.filterSelect} ${schedulerStyle.filterRobot}`}
              value={filterState.robotSn}
              onChange={e => setFilterState(s => ({ ...s, robotSn: e.target.value }))}
            >
              <option value="">All robots</option>
              {filterOptions.robots.map(r => (
                <option key={r.sn} value={r.sn}>
                  {r.name}
                </option>
              ))}
            </select>

            <select
              className={`${schedulerStyle.filterSelect} ${schedulerStyle.filterMission}`}
              value={filterState.missionKey}
              onChange={e => setFilterState(s => ({ ...s, missionKey: e.target.value }))}
            >
              <option value="">All missions</option>
              {scenarioMissionOptions.length > 0 && (
                <optgroup label={t('dashboard.filters.scenarios_group')}>
                  {scenarioMissionOptions.map(m => (
                    <option key={`scenario-${m.key}`} value={m.key}>
                      {m.label}
                    </option>
                  ))}
                </optgroup>
              )}
              {missionOptions.length > 0 && (
                <optgroup label={t('dashboard.filters.missions_group')}>
                  {missionOptions.map(m => (
                    <option key={`mission-${m.key}`} value={m.key}>
                      {m.label}
                    </option>
                  ))}
                </optgroup>
              )}
              {mapOptions.length > 0 && (
                <optgroup label={t('dashboard.filters.maps_group')}>
                  {mapOptions.map(m => (
                    <option key={`map-${m.key}`} value={m.key}>
                      {m.label}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>

            <select
              className={`${schedulerStyle.filterSelect} ${schedulerStyle.filterTime}`}
              value={filterState.startAfterHour}
              onChange={e => setFilterState(s => ({ ...s, startAfterHour: Number(e.target.value) }))}
            >
              <option value={-1}>Start after: all</option>
              {Array.from({ length: 24 }).map((_, i) => (
                <option key={i} value={i}>
                  Start after: {i < 10 ? `0${i}` : i}:00
                </option>
              ))}
            </select>

            <CustomButton.Outlined className={schedulerStyle.clearButton} onClick={handleClearFilters}>
              Clear filters
            </CustomButton.Outlined>
          </div>
        </div>

        <div className={schedulerStyle.calendarWrapper}>
          <Calendar
            localizer={localizer}
            events={agendaEvents}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%' }}
            view={view}
            onView={handleViewChange}
            date={selectedDate}
            onNavigate={handleNavigate}
            length={view === Views.AGENDA ? 1 : undefined}
            onRangeChange={(range: { start: Date; end: Date } | Date[]) => {
              const newStart = Array.isArray(range) ? range[0] : range.start
              const newEnd = Array.isArray(range) ? range[range.length - 1] : range.end

              if (newStart && newEnd) {
                const s = newStart.getTime()
                const e = newEnd.getTime()

                setCurrentRange(prev => {
                  if (prev.start.getTime() === s && prev.end.getTime() === e) {
                    return prev
                  }
                  return { start: new Date(s), end: new Date(e) }
                })
              }
            }}
            eventPropGetter={eventPropGetter}
            onSelectEvent={handleEventClick}
            components={{
              event: ({ event }: { event: ProcessedEvent }) => (
                <div
                  title={event.title}
                  style={{ fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', pointerEvents: 'none' }}
                >
                  {event.title}
                </div>
              ),
            }}
          />
        </div>

        <Popover
          open={Boolean(anchorEl) && Boolean(popoverEvent)}
          anchorEl={anchorEl}
          onClose={handleClosePopover}
          disableRestoreFocus
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{
            sx: { p: 0, borderRadius: 2, minWidth: 280, maxWidth: 400 },
          }}
        >
          {popoverEvent && (
            <Box>
              <Box sx={{ height: '6px', bgcolor: popoverEvent.color || '#3174ad' }} />
              <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {popoverEvent.title}
                  </Typography>
                  <IconButton size="small" onClick={handleClosePopover}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
                <TaskEventViewer event={popoverEvent} />
                <Divider sx={{ my: 1.5 }} />
                {popoverEvent.extendedProps?.source_kind === 'planned' && popoverEvent.end.getTime() > Date.now() && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 1.5 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EditIcon fontSize="small" />}
                      sx={{ borderRadius: 0, textTransform: 'none', px: 2 }}
                      onClick={() => {
                        handleClosePopover()
                        const isSingle = isAllValueDisabled(popoverEvent)
                        if (isSingle) {
                          setSingleTaskEditing(true)
                          setIsSettingsOpen(true)
                          setEventScope(EventScopes.CURRENT)
                        } else {
                          setIsEditConfirmationOpen(true)
                        }
                      }}
                    >
                      {t('common.edit')}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon fontSize="small" />}
                      sx={{ borderRadius: 0, textTransform: 'none', px: 2 }}
                      onClick={() => {
                        handleClosePopover()
                        setDeleteDescription('')
                        setDeleteReason('')
                        setOpenDeleteScheduleDialog(true)
                      }}
                    >
                      {t('common.delete')}
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Popover>
        {isEditConfirmationOpen && (
          <ConfirmationDialogAllSingle
            confirmText={'Ok'}
            cancelText="Cancel"
            open={true}
            title={'Edit the scheduled task?'}
            submitting={undefined}
            onClose={() => {
              setIsEditConfirmationOpen(false)
              setEventScope(EventScopes.CURRENT)
            }}
            onSubmit={() => {
              if (eventScope === EventScopes.ALL) setSingleTaskEditing(false)
              else if (eventScope === EventScopes.CURRENT) setSingleTaskEditing(true)
              else throw new Error(`Unknown event scope: ${eventScope}`)
              setIsSettingsOpen(true)
              setIsEditConfirmationOpen(false)
              setEventScope(EventScopes.CURRENT)
            }}
          >
            <EventEditDeletePopup
              currentValue={EventScopes.CURRENT}
              allValue={EventScopes.ALL}
              value={eventScope}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setEventScope(event.target.value)}
            />
          </ConfirmationDialogAllSingle>
        )}
        <TaskSettingsModal
          isOpen={isSettingsOpen}
          onClose={onSettingsCloseClicked}
          taskName={'Cleaning Task'}
          taskToEdit={popoverEvent ? getTaskById(String(popoverEvent.event_id)) : undefined}
          eventScope={isSingleTaskEditing ? 'current' : 'all'}
          dateToEdit={isSingleTaskEditing && popoverEvent ? popoverEvent.start : null}
          onSubmit={onSettingsSubmitClicked}
        />
        {openDeleteScheduleDialog && (
          <ConfirmationDialogAllSingle
            confirmText={isAllValueDisabled(popoverEvent) ? t('common.delete') : 'Ok'}
            cancelText="Cancel"
            open={openDeleteScheduleDialog}
            isSmall={isAllValueDisabled(popoverEvent)}
            danger={true}
            title={isAllValueDisabled(popoverEvent) ? 'Delete the task?' : 'Delete the scheduled task?'}
            disableConfirm={
              !isAllValueDisabled(popoverEvent) &&
              eventScope === EventScopes.CURRENT &&
              (!deleteReason || (deleteReason === 'Other' && !deleteDescription))
            }
            submitting={undefined}
            onClose={() => {
              setOpenDeleteScheduleDialog(false)
              setEventScope(EventScopes.CURRENT)
              setDeleteDescription('')
              setDeleteReason('')
            }}
            onSubmit={handleSubmitDeleteSchedule}
          >
            {!isAllValueDisabled(popoverEvent) && (
              <>
                <EventEditDeletePopup
                  currentValue={EventScopes.CURRENT}
                  allValue={EventScopes.ALL}
                  value={eventScope}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => setEventScope(event.target.value)}
                />
                {eventScope === EventScopes.CURRENT && (
                  <Box sx={{ marginBottom: '30px' }}>
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel id="delete-reason-label">Reason</InputLabel>
                      <Select
                        labelId="delete-reason-label"
                        value={deleteReason}
                        label="Reason"
                        onChange={e => setDeleteReason(e.target.value)}
                      >
                        <MenuItem value="Weather Conditions">Weather Conditions</MenuItem>
                        <MenuItem value="Busy Area">Busy Area</MenuItem>
                        <MenuItem value="Maintenance">Maintenance</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                      </Select>
                    </FormControl>
                    {deleteReason === 'Other' && (
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="Description"
                        value={deleteDescription}
                        onChange={e => setDeleteDescription(e.target.value)}
                        placeholder="Please provide a reason..."
                        required
                      />
                    )}
                  </Box>
                )}
              </>
            )}
          </ConfirmationDialogAllSingle>
        )}
      </div>
    </>
  )
}

export default TaskSchedulerNormalized
